import { useMemo, useState, useEffect, memo, useCallback } from 'react';
import { View, FlatList, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { ItineraryItemCompactCard } from './ItineraryItemCompactCard';
import { ItineraryItemDetailsSheet } from './ItineraryItemDetailsSheet';
import { FillYourDay } from './FillYourDay';
import { groupItineraryItemsByDate } from '@/services/itinerary';
import { fromLocalDateKey, toLocalDateKey } from '@/utils/dateKeys';
import type { ItineraryItem } from '@/services/itinerary';
import type { Booking } from '@/services/bookings';
import type { Trip, TripDestination } from '@/services/trips';
import type { WeatherAlert } from './DayWeatherSheet';
import { getCachedTravelTime } from '@/utils/travelTimeCache';
import { estimateTravelTime } from '@/utils/itineraryScheduling';
import { toIso2CountryCode } from '@/utils/countryIso2';

const EMPTY_DAY_ITEMS: ItineraryItem[] = [];
const EMPTY_ALERTS: WeatherAlert[] = [];

type VerticalFlowViewProps = {
  items: ItineraryItem[];
  bookings?: Booking[];
  preferredTravelMode?: 'walking' | 'driving' | 'transit';
  onEditItem?: (item: ItineraryItem) => void;
  onDeleteItem?: (item: ItineraryItem) => void;
  onViewDocument?: (url: string, meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null }) => void;
  canEdit?: boolean;
  startDate?: Date; // Trip start date
  endDate?: Date; // Trip end date (used to render empty days)
  onAddItem?: (date?: Date) => void;
  trip?: Trip;
  onOpenIdeas?: (preferredTab?: 'ai' | 'localPicks' | 'amadeus' | 'for_you', targetDate?: Date) => void;
  tripDestination?: string;
  tripDestinations?: TripDestination[]; // Multi-city destinations
  dailyWeather?: Record<
    string,
    {
      iconName: string;
      label: string;
      tempMin?: number;
      tempMax?: number;
      precipProb?: number;
      windSpeed?: number;
      condition?: string;
      description?: string;
      source?: 'forecast' | 'historical';
    }
  >;
  dailyTimezone?: Record<string, { label: string; timezone?: string }>;
  dailyAlerts?: Record<string, WeatherAlert[]>;
  onOpenDayWeather?: (dateKey: string) => void;
  onResolveConflict?: (conflictItem: ItineraryItem) => void;
  tripId?: string; // For voting
  isTripActive?: boolean;
  onOpenMobility?: (itemId: string) => void;
};

const formatTime = (time?: string): string | null => {
  if (!time || time === 'flexible') return null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return null;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes || '00'} ${ampm}`;
};

const formatDuration = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const withTimeOnDate = (date: Date, time: string): Date | null => {
  if (!time || time === 'flexible') return null;
  const [h, m] = time.split(':').map((v) => parseInt(v, 10));
  if (!Number.isFinite(h)) return null;
  const dt = new Date(date);
  dt.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
  return dt;
};

const withAlpha = (color: string, alphaHex: string) => {
  if (typeof color === 'string' && color.startsWith('#') && color.length === 7) return `${color}${alphaHex}`;
  return color;
};

type VerticalFlowDaySectionProps = {
  dateKey: string;
  date: Date;
  dateItems: ItineraryItem[];
  allItems: ItineraryItem[];
  newCity: TripDestination | null;
  weather?: VerticalFlowViewProps['dailyWeather'] extends Record<string, infer V> ? V : any;
  tz?: { label: string; timezone?: string };
  alerts?: WeatherAlert[];
  preferredTravelMode?: 'walking' | 'driving' | 'transit';
  canEdit: boolean;
  showFill: boolean;
  onToggleFill: () => void;
  onAddItem?: (date?: Date) => void;
  onOpenIdeas?: (preferredTab?: 'ai' | 'localPicks' | 'amadeus' | 'for_you', targetDate?: Date) => void;
  trip?: Trip;
  onOpenDayWeather?: (dateKey: string) => void;
  openDetails: (item: ItineraryItem) => void;
};

const VerticalFlowDaySection = memo(function VerticalFlowDaySection({
  dateKey,
  date,
  dateItems,
  allItems,
  newCity,
  weather,
  tz,
  alerts = [],
  preferredTravelMode,
  canEdit,
  showFill,
  onToggleFill,
  onAddItem,
  onOpenIdeas,
  trip,
  onOpenDayWeather,
  openDetails,
}: VerticalFlowDaySectionProps) {
  const theme = useTheme();
  const railDot = theme.colors.textSecondary;
  const railLine = withAlpha(theme.colors.textSecondary, '70');
  const topAlert = alerts[0];
  const alertColor =
    topAlert?.severity === 'critical'
      ? theme.colors.error
      : topAlert?.severity === 'high'
        ? theme.colors.warning
        : topAlert?.severity === 'medium'
          ? theme.colors.primary
          : theme.colors.textSecondary;
  const alertSeverityLabel =
    topAlert?.severity === 'critical'
      ? 'Critical'
      : topAlert?.severity === 'high'
        ? 'High'
        : topAlert?.severity === 'medium'
          ? 'Moderate'
          : null;

  const isEmpty = dateItems.length === 0;

  const sortedItems = useMemo(() => {
    return [...dateItems].sort((a, b) => {
      const aIsFlexible = !a.startTime || a.startTime === 'flexible';
      const bIsFlexible = !b.startTime || b.startTime === 'flexible';
      if (aIsFlexible && bIsFlexible) return 0;
      if (aIsFlexible) return 1;
      if (bIsFlexible) return -1;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [dateItems]);

  const [gapTravelTimes, setGapTravelTimes] = useState<Record<string, number | null>>({});
  const [gapEstimating, setGapEstimating] = useState<Record<string, boolean>>({});

  const getItemCountryIso2 = useCallback(
    (item: ItineraryItem): string | undefined => {
      const destinations = trip?.destinations;
      if (!destinations || destinations.length === 0) return undefined;
      const idx = typeof item.cityIndex === 'number' ? item.cityIndex : null;
      const d = (idx != null && destinations[idx]) ? destinations[idx] : destinations[0];
      const raw = (d as any)?.countryCode || (d as any)?.country;
      if (typeof raw !== 'string' || !raw.trim()) return undefined;
      const iso2 = toIso2CountryCode(raw);
      return iso2 || (raw.trim().length === 2 ? raw.trim().toUpperCase() : undefined);
    },
    [trip?.destinations],
  );
  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      const nextMap: Record<string, number | null> = {};
      const toEstimate: Array<{ key: string; fromLoc: string; toLoc: string; departAt: Date; country?: string }> = [];
      
      for (let i = 0; i < sortedItems.length - 1; i++) {
        const a = sortedItems[i];
        const b = sortedItems[i + 1];
        if (!a.endTime || !b.startTime || b.startTime === 'flexible') continue;
        const end = withTimeOnDate(a.startDate, a.endTime);
        if (!end) continue;
        const fromLoc = a.location || a.address;
        const toLoc = b.location || b.address;
        if (!fromLoc || !toLoc) continue;

        const key = `${a.id}->${b.id}`;
        const mode = (preferredTravelMode || 'driving') as any;
        const country = getItemCountryIso2(b) || getItemCountryIso2(a);
        const cached = await getCachedTravelTime(fromLoc, toLoc, mode, end, country);
        
        if (cached !== null) {
          nextMap[key] = cached;
        } else {
          // Queue for estimation (limit to first 5 to avoid too many API calls)
          if (toEstimate.length < 5) {
            toEstimate.push({ key, fromLoc, toLoc, departAt: end, country });
          }
          nextMap[key] = null;
        }
      }

      if (!cancelled) setGapTravelTimes(nextMap);
      
      // Auto-estimate uncached travel times in background (limit concurrent calls)
      if (!cancelled && toEstimate.length > 0) {
        const mode = preferredTravelMode || 'driving';
        const defaultTimes: Record<string, number> = { walking: 20, driving: 25, transit: 30 };
        
        for (const { key, fromLoc, toLoc, departAt, country } of toEstimate) {
          if (cancelled) break;
          try {
            const mins = await estimateTravelTime(fromLoc, toLoc, {
              mode,
              departureTime: departAt,
              defaultTime: defaultTimes[mode] || 25,
              country,
            });
            if (!cancelled) {
              setGapTravelTimes((prev) => ({ ...prev, [key]: mins }));
            }
          } catch {
            // Silently ignore errors - user can still tap to retry
          }
        }
      }
    };

    compute();
    return () => {
      cancelled = true;
    };
  }, [sortedItems, preferredTravelMode, dateKey, getItemCountryIso2]);

  const handleEstimateGapTravel = async (from: ItineraryItem, to: ItineraryItem, departAt: Date, key: string) => {
    if (gapEstimating[key]) return;
    setGapEstimating((prev) => ({ ...prev, [key]: true }));
    await new Promise((r) => setTimeout(r, 0));
    try {
      const mode = preferredTravelMode || 'driving';
      // Use mode-aware default times
      const defaultTimes = { walking: 20, driving: 25, transit: 30 };
      const country = getItemCountryIso2(to) || getItemCountryIso2(from);
      const mins = await estimateTravelTime(from.location || from.address, to.location || to.address, {
        mode,
        departureTime: departAt,
        defaultTime: defaultTimes[mode] || 25,
        country,
      });
      setGapTravelTimes((prev) => ({ ...prev, [key]: mins }));
    } finally {
      setGapEstimating((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <View style={{ gap: theme.spacing(1.5), paddingBottom: theme.spacing(3) }}>
      {/* City Transition Header */}
      {newCity && (
        <View
          style={{
            padding: theme.spacing(2),
            borderRadius: theme.radii.lg,
            backgroundColor: theme.colors.primary + '15',
            borderWidth: 2,
            borderColor: theme.colors.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5) }}>
            <Ionicons name="airplane" size={20} color={theme.colors.primary} />
            <ThemedText variant="bodyStrong" style={{ fontSize: 16, color: theme.colors.primary }}>
              Now in: {newCity.city}
            </ThemedText>
          </View>
          <Caption color={theme.colors.textSecondary}>
            {new Date(newCity.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
            {new Date(newCity.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </Caption>
        </View>
      )}

      {/* Date Header */}
      <View
        style={{
          paddingBottom: theme.spacing(1),
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderMuted,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <SubHeading style={{ fontSize: 18, flex: 1 }}>
            {date.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
            })}
          </SubHeading>
          {(weather || tz) && (
            <Pressable
              onPress={() => {
                if (weather && onOpenDayWeather) onOpenDayWeather(dateKey);
              }}
              disabled={!weather || !onOpenDayWeather}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.pill,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                backgroundColor: theme.colors.surface,
              }}
              accessibilityRole={weather && onOpenDayWeather ? 'button' : undefined}
              accessibilityLabel={weather && onOpenDayWeather ? 'Open weather details' : undefined}
              accessibilityHint={weather && onOpenDayWeather ? 'Shows weather details for this day' : undefined}
            >
              {weather && <Ionicons name={(weather as any).iconName as any} size={14} color={theme.colors.textSecondary} />}
              {weather && (
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
                  {(weather as any).label}
                </Caption>
              )}
              {!!topAlert && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                  <Ionicons
                    name="alert-circle-outline"
                    size={14}
                    color={alertColor}
                    accessibilityLabel={`${alertSeverityLabel || 'Weather'} alert`}
                  />
                  {!!alertSeverityLabel && (
                    <Caption color={alertColor} style={{ fontSize: 10, fontWeight: '700' }}>
                      {alertSeverityLabel}
                    </Caption>
                  )}
                </View>
              )}
              {tz && (
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
                  {weather ? ` • ${tz.label}` : tz.label}
                </Caption>
              )}
            </Pressable>
          )}
        </View>
        <Body color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: theme.spacing(0.25) }}>
          {dateItems.length} {dateItems.length === 1 ? 'item' : 'items'}
        </Body>
      </View>

      {/* Itinerary Items / Empty Day */}
      {isEmpty ? (
        <View
          style={{
            padding: theme.spacing(2),
            borderRadius: theme.radii.lg,
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          <Ionicons name="calendar-outline" size={28} color={theme.colors.textSecondary} />
          <ThemedText variant="bodyStrong">No items for this day</ThemedText>
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Add an activity, flight, or stay—then we’ll keep everything in sync.
          </Body>

          {canEdit && onAddItem && (
            <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(1), width: '100%' }}>
              <Button
                label="Add item"
                variant="primary"
                onPress={() => onAddItem(date)}
                icon={<Ionicons name="add-outline" size={18} color={theme.colors.onPrimary} />}
                style={{ flex: 1 }}
              />

              {onOpenIdeas && (
                <Button
                  label="Add from ideas"
                  variant="secondary"
                  onPress={() => onOpenIdeas('localPicks', date)}
                  icon={<Ionicons name="bulb-outline" size={18} color={theme.colors.text} />}
                  style={{ flex: 1 }}
                />
              )}

              {trip && !onOpenIdeas && (
                <Button
                  label={showFill ? 'Hide suggestions' : 'Fill this day'}
                  variant="secondary"
                  onPress={onToggleFill}
                  icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />}
                  style={{ flex: 1 }}
                />
              )}
            </View>
          )}

          {canEdit && trip && (
            <Button
              label={showFill ? 'Hide suggestions' : 'Fill this day'}
              variant="secondary"
              onPress={onToggleFill}
              icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />}
            />
          )}

          {canEdit && trip && showFill && (
            <View style={{ width: '100%', height: 420, marginTop: theme.spacing(1) }}>
              <FillYourDay
                trip={trip}
                day={date}
                existingItems={allItems}
                onItemAdded={() => {
                  // Items will refresh automatically via subscription
                }}
              />
            </View>
          )}
        </View>
      ) : (
        <View style={{ gap: theme.spacing(1.25) }}>
          {sortedItems.map((it, idx) => {
            const isLast = idx === sortedItems.length - 1;
            const startLabel = it.startTime ? formatTime(it.startTime) : null;
            const next = sortedItems[idx + 1];
            const canRenderGap = !!it.endTime && !!next?.startTime && next.startTime !== 'flexible';
            const gapStart = canRenderGap ? withTimeOnDate(it.startDate, it.endTime!) : null;
            const gapEnd = canRenderGap ? withTimeOnDate(next.startDate, next.startTime!) : null;
            const gapMinutes = gapStart && gapEnd ? Math.round((gapEnd.getTime() - gapStart.getTime()) / (1000 * 60)) : null;
            const gapKey = next ? `${it.id}->${next.id}` : null;
            const travelMinutes = gapKey ? (gapTravelTimes[gapKey] ?? null) : null;
            const isEstimating = gapKey ? Boolean(gapEstimating[gapKey]) : false;
            const showGap = !!gapMinutes && gapMinutes > 0 && gapStart && gapEnd;
            const bufferMinutes = showGap && typeof travelMinutes === 'number' ? gapMinutes - travelMinutes : null;

            return (
              <View key={it.id} style={{ gap: theme.spacing(1.25) }}>
                <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: theme.spacing(1.25) }}>
                  <View style={{ width: 62, paddingTop: 4 }}>
                    <ThemedText variant="caption" style={{ fontSize: 12, fontWeight: '700' }}>
                      {startLabel || '—'}
                    </ThemedText>
                  </View>

                  <View style={{ width: 16, alignItems: 'center' }}>
                    <View
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: railDot,
                        marginTop: 8,
                      }}
                    />
                    <View
                      style={{
                        flex: 1,
                        width: 2,
                        backgroundColor: isLast ? 'transparent' : railLine,
                        marginTop: 6,
                      }}
                    />
                  </View>

                  <View style={{ flex: 1 }}>
                    <ItineraryItemCompactCard
                      item={it}
                      tripDestinations={trip?.destinations || undefined}
                      contextDateKey={dateKey}
                      onPress={() => openDetails(it)}
                      showTimeRange
                      showSecondaryLine
                    />
                  </View>
                </View>

                {showGap && next && (
                  <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: theme.spacing(1.25) }}>
                    <View style={{ width: 62 }} />
                    <View style={{ width: 16, alignItems: 'center' }}>
                      <View style={{ flex: 1, width: 2, backgroundColor: withAlpha(theme.colors.textSecondary, '40') }} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <View
                        style={{
                          padding: theme.spacing(1.25),
                          borderRadius: theme.radii.lg,
                          backgroundColor: theme.colors.borderMuted + '18',
                          borderWidth: 1,
                          borderColor: theme.colors.borderMuted + '35',
                          gap: 4,
                        }}
                      >
                        <ThemedText variant="bodyStrong" style={{ fontSize: 13, fontWeight: '700' }}>
                          {formatTime(it.endTime!) || '—'}–{formatTime(next.startTime!) || '—'} • {formatDuration(gapMinutes)}
                        </ThemedText>
                        {typeof travelMinutes === 'number' && (
                          <Caption style={{ color: theme.colors.textSecondary }}>
                            Travel: {formatDuration(travelMinutes)} to {next.title}
                            {typeof bufferMinutes === 'number' && bufferMinutes > 0 ? ` • Buffer: ${formatDuration(bufferMinutes)}` : ''}
                          </Caption>
                        )}
                        {travelMinutes === null && (
                          <Pressable
                            onPress={() => {
                              if (!gapKey || !gapStart) return;
                              const fromLoc = it.location || it.address;
                              const toLoc = next.location || next.address;
                              if (!fromLoc || !toLoc) return;
                              void handleEstimateGapTravel(it, next, gapStart, gapKey);
                            }}
                            disabled={isEstimating || !gapKey || !gapStart}
                            style={({ pressed }) => ({
                              opacity: pressed ? 0.7 : 1,
                              alignSelf: 'flex-start',
                            })}
                            accessibilityRole="button"
                            accessibilityLabel={isEstimating ? 'Estimating travel time' : 'Estimate travel time'}
                            accessibilityHint="Calculates travel time between these activities"
                          >
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                              <Ionicons
                                name={isEstimating ? 'time-outline' : 'sparkles-outline'}
                                size={14}
                                color={theme.colors.textSecondary}
                                accessible={false}
                              />
                              <Caption style={{ color: theme.colors.textSecondary }}>
                                {isEstimating ? 'Estimating travel…' : `Travel: — to ${next.title} • Tap to estimate`}
                              </Caption>
                            </View>
                          </Pressable>
                        )}
                      </View>
                    </View>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
});

export function VerticalFlowView({
  items,
  bookings = [],
  preferredTravelMode,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  canEdit = false,
  startDate,
  endDate,
  onAddItem,
  trip,
  onOpenIdeas,
  tripDestination,
  tripDestinations,
  dailyWeather,
  dailyTimezone,
  dailyAlerts,
  onOpenDayWeather,
  onResolveConflict,
  tripId,
  isTripActive = false,
  onOpenMobility,
}: VerticalFlowViewProps) {
  const theme = useTheme();
  const groupedByDate = groupItineraryItemsByDate(items);
  const PREVIEW_DAYS = 2;
  const bookingLookup = useMemo(() => {
    const map = new Map<string, Booking>();
    bookings.forEach((booking) => {
      map.set(booking.id, booking);
    });
    return map;
  }, [bookings]);

  const allDates = useMemo(() => {
    if (!startDate || !endDate) {
      return Array.from(groupedByDate.keys()).sort();
    }

    const keys: string[] = [];
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const endLocal = new Date(endDate);
    endLocal.setHours(0, 0, 0, 0);
    while (cursor <= endLocal) {
      keys.push(toLocalDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return keys;
  }, [groupedByDate, startDate, endDate]);

  const [expandedEmptyDayKey, setExpandedEmptyDayKey] = useState<string | null>(null);
  useEffect(() => {
    // Collapse suggestions when the list range changes (trip changed).
    setExpandedEmptyDayKey(null);
  }, [startDate, endDate, tripId]);

  // Prevent an overly long scroll by default: show the first 2 days, with a "See more" toggle.
  const [showAllDays, setShowAllDays] = useState(false);
  useEffect(() => {
    // Reset when switching trips / ranges.
    setShowAllDays(false);
  }, [startDate, endDate, tripId]);

  const visibleDates = useMemo(() => {
    if (showAllDays) return allDates;
    return allDates.slice(0, PREVIEW_DAYS);
  }, [allDates, showAllDays]);
  const hiddenDayCount = Math.max(0, allDates.length - visibleDates.length);

  // Details sheet state (shared across the list)
  const [selectedItemForDetails, setSelectedItemForDetails] = useState<ItineraryItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  const openDetails = useCallback((item: ItineraryItem) => {
    setSelectedItemForDetails(item);
    setDetailsVisible(true);
  }, []);

  const closeDetails = useCallback(() => setDetailsVisible(false), []);

  // Helper function to determine which city a date belongs to
  const getCityForDate = useCallback((date: Date): TripDestination | null => {
    if (!tripDestinations || tripDestinations.length <= 1) return null;
    
    return tripDestinations.find(dest => {
      const destStart = new Date(dest.startDate);
      const destEnd = new Date(dest.endDate);
      destStart.setHours(0, 0, 0, 0);
      destEnd.setHours(23, 59, 59, 999);
      return date >= destStart && date <= destEnd;
    }) || null;
  }, [tripDestinations]);

  const cityTransitionByDateKey = useMemo(() => {
    const transitions = new Map<string, TripDestination | null>();
    if (!tripDestinations || tripDestinations.length <= 1) {
      return transitions;
    }
    allDates.forEach((dateKey, index) => {
      const currentCity = getCityForDate(fromLocalDateKey(dateKey));
      if (index === 0) {
        transitions.set(dateKey, currentCity);
        return;
      }
      const prevDateKey = allDates[index - 1];
      const prevCity = getCityForDate(fromLocalDateKey(prevDateKey));
      transitions.set(
        dateKey,
        currentCity && prevCity && currentCity.city !== prevCity.city ? currentCity : null,
      );
    });
    return transitions;
  }, [allDates, getCityForDate, tripDestinations]);

  const dayAlertsByDateKey = useMemo(() => {
    const alertsByDateKey = new Map<string, WeatherAlert[]>();
    visibleDates.forEach((dateKey) => {
      alertsByDateKey.set(dateKey, dailyAlerts?.[dateKey] ?? EMPTY_ALERTS);
    });
    return alertsByDateKey;
  }, [dailyAlerts, visibleDates]);

  const hasTripRange = !!startDate && !!endDate;
  const showGlobalEmpty = items.length === 0 && !hasTripRange;

  const renderDay = ({ item: dateKey }: { item: string; index: number }) => {
    const dateItems = groupedByDate.get(dateKey) ?? EMPTY_DAY_ITEMS;
    const date = fromLocalDateKey(dateKey);
    const newCity = cityTransitionByDateKey.get(dateKey) ?? null;
    const weather = dailyWeather?.[dateKey];
    const tz = dailyTimezone?.[dateKey];
    const alerts = dayAlertsByDateKey.get(dateKey) ?? EMPTY_ALERTS;
    const showFill = expandedEmptyDayKey === dateKey;

    return (
      <VerticalFlowDaySection
        dateKey={dateKey}
        date={date}
        dateItems={dateItems}
        allItems={items}
        newCity={newCity}
        weather={weather as any}
        tz={tz}
        alerts={alerts}
        preferredTravelMode={preferredTravelMode}
        canEdit={canEdit}
        showFill={showFill}
        onToggleFill={() => setExpandedEmptyDayKey(showFill ? null : dateKey)}
        onAddItem={onAddItem}
        onOpenIdeas={onOpenIdeas}
        trip={trip}
        onOpenDayWeather={onOpenDayWeather}
        openDetails={openDetails}
      />
    );
  };

  if (showGlobalEmpty) {
    return (
      <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(1.5) }}>
        <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
          No itinerary items
        </ThemedText>
        <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
          Add activities, flights, or stays to start planning your trip.
        </Body>
      </View>
    );
  }

  const selectedDetailsDateKey = selectedItemForDetails ? toLocalDateKey(selectedItemForDetails.startDate) : null;
  const detailsDayTimezone = selectedDetailsDateKey ? dailyTimezone?.[selectedDetailsDateKey]?.timezone : undefined;
  const detailsCurrentCity = selectedItemForDetails ? getCityForDate(selectedItemForDetails.startDate)?.city : undefined;

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        data={visibleDates}
        keyExtractor={(k) => k}
        renderItem={renderDay}
        contentContainerStyle={{
          padding: theme.spacing(2),
          paddingBottom: theme.spacing(4),
        }}
        ListFooterComponent={
          allDates.length > PREVIEW_DAYS ? (
            <View style={{ paddingTop: theme.spacing(1) }}>
              <Button
                label={
                  showAllDays
                    ? 'Show fewer days'
                    : hiddenDayCount > 0
                      ? `See ${hiddenDayCount} more day${hiddenDayCount === 1 ? '' : 's'}`
                      : 'See more'
                }
                variant="secondary"
                onPress={() => setShowAllDays((v) => !v)}
                icon={
                  <Ionicons
                    name={showAllDays ? 'chevron-up' : 'chevron-down'}
                    size={18}
                    color={theme.colors.text}
                  />
                }
              />
            </View>
          ) : null
        }
        initialNumToRender={10}
        maxToRenderPerBatch={12}
        windowSize={8}
        removeClippedSubviews
      />

      <ItineraryItemDetailsSheet
        visible={detailsVisible}
        item={selectedItemForDetails}
        booking={selectedItemForDetails?.bookingId ? bookingLookup.get(selectedItemForDetails.bookingId) : undefined}
        allItems={items}
        preferredTravelMode={preferredTravelMode}
        onClose={closeDetails}
        onEditItem={onEditItem}
        onDeleteItem={onDeleteItem}
        onViewDocument={onViewDocument}
        canEdit={canEdit}
        tripStartDate={startDate}
        tripDestination={tripDestination}
        currentCity={detailsCurrentCity}
        dayTimezone={detailsDayTimezone}
        onResolveConflict={onResolveConflict}
        tripId={tripId}
        trip={trip}
        isTripActive={isTripActive}
        onOpenMobility={
          onOpenMobility
            ? (itemId) => {
                closeDetails();
                onOpenMobility(itemId);
              }
            : undefined
        }
      />
    </View>
  );
}

